import { Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen, Clock } from "lucide-react";

import { resourceBySlug, resourcePath, resourceTitle } from "@/lib/resources";
import { pathFor, useLocale, type Locale } from "@/lib/i18n";

const TEXT: Record<
  Locale,
  {
    eyebrow: string;
    heading: string;
    body: string;
    cta: string;
    all: string;
    minutes: string;
  }
> = {
  sv: {
    eyebrow: "Fördjupning",
    heading: "Den här guiden ger översikten – Kunskapsbanken går på djupet",
    body: "Guiderna på sajten är till för att ge en samlad bild och svara på de vanligaste frågorna. Kunskapsbanken är något annat: steg för steg, med checklistor, tidsplaner, blanketter och kontaktuppgifter att arbeta efter när flytten är beslutad.",
    cta: "Läs vidare i Kunskapsbanken",
    all: "Se hela kunskapsbanken",
    minutes: "min läsning",
  },
  en: {
    eyebrow: "Go deeper",
    heading: "This guide gives the overview – the Knowledge Hub goes deeper",
    body: "The guides on this site are here to give you the full picture and answer the most common questions. The Knowledge Hub is something else: step by step, with checklists, timelines, forms and contact details to work from once the move is decided.",
    cta: "Continue in the Knowledge Hub",
    all: "See the whole Knowledge Hub",
    minutes: "min read",
  },
  es: {
    eyebrow: "Profundiza",
    heading: "Esta guía da la visión general; el centro de recursos profundiza",
    body: "Las guías del sitio ofrecen una visión de conjunto y responden a las preguntas más habituales. El centro de recursos es otra cosa: paso a paso, con listas de comprobación, calendarios, formularios y contactos para cuando la mudanza ya está decidida.",
    cta: "Seguir en el centro de recursos",
    all: "Ver todo el centro de recursos",
    minutes: "min de lectura",
  },
};

/**
 * Hänvisning från en öppen guide till det fördjupade materialet i kunskapsbanken,
 * så att guiderna kompletterar – inte konkurrerar med – kunskapsbanken.
 */
export function KnowledgeHubNote({
  resource: slug,
  locale,
}: {
  /** Svensk slug för det kunskapsmaterial som ligger närmast guiden. */
  resource: string;
  locale?: Locale;
}) {
  const urlLocale = useLocale();
  const loc = locale ?? urlLocale;
  const t = TEXT[loc] ?? TEXT.sv;
  const resource = resourceBySlug(slug);
  if (!resource) return null;

  return (
    <aside className="mx-auto max-w-3xl px-5 pb-12 sm:pb-16">
      <div className="rounded-xl border border-border bg-sand p-6 sm:p-7">
        <p className="flex items-center gap-2 text-xs font-semibold tracking-[0.18em] text-primary uppercase">
          <BookOpen className="size-4" aria-hidden="true" /> {t.eyebrow}
        </p>
        <h2 className="mt-3 text-xl leading-snug font-semibold sm:text-2xl">{t.heading}</h2>
        <p className="mt-3 leading-relaxed text-muted-foreground">{t.body}</p>
        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
          <Link
            to={resourcePath(resource, loc) as never}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
          >
            {t.cta} <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
          <Link
            to={pathFor("/kunskapsbank", loc) as never}
            className="inline-flex min-h-11 items-center justify-center text-sm font-medium text-primary link-quiet"
          >
            {t.all}
          </Link>
        </div>
        <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="size-3.5" aria-hidden="true" />
          <span>
            {resourceTitle(resource, loc)} · {resource.minutes} {t.minutes}
          </span>
        </p>
      </div>
    </aside>
  );
}
